import {ReactElement} from "react";
import {createStore} from "zustand-x";
import i18n from "../i18n/i18n";

/** Supported dialog types */
export enum DialogType {
    Simple,
    ActivityCategory,
    ActivityType,
    Address,
    Answer,
    BankAccount,
    Blackout,
    Campaign,
    CaseType,
    Category,
    Comment,
    Conflict,
    Contact,
    Discount,
    Docket,
    Document,
    EntityTimestamp,
    Event,
    Expense,
    Firm,
    FlatFee,
    FlatFeeMilestone,
    Group,
    Identification,
    ImportExport,
    Invoice,
    InvoiceBatch,
    InvoiceBatchItem,
    Lead,
    Lookup,
    Matter,
    Milestone,
    Option,
    Participant,
    Privilege,
    Question,
    RedFlag,
    RedFlagType,
    Referral,
    Resource,
    Role,
    Task,
    TemplateTask,
    TimeOff,
    Transaction,
    User,
    // Appended (not inserted) so existing numeric values don't shift —
    // these are referenced at runtime, but renumbering is still risky.
    Deadline,
}

/** Props for the dialog type. Change to a union type once we get more of these built out. */
export type DialogProps = any;

export type SimpleDialogProps = {
    title: string,
    message: DialogMessage,
    okLabel?: string,
    cancelLabel?: string,
    background?: string,
    noButtons?: boolean
}

export type DialogMessage = string | ReactElement;

export type DialogState = {
    /** If the dialog should show a title, set that here */
    title?: string,

    /** If this is a simple dialog (DialogType === '_'), we will just show a message */
    message?: string | ReactElement,

    /** What kind of dialog are we showing? */
    dialogType: DialogType;

    background?: string,

    /** What props need to be passed to the dialog? */
    componentProps?: any,

    /** If the dialog returns values, they go here */
    dialogResponse?: any,

    /** Handle height and width */
    fitHeight?: boolean,
    fitWidth?: boolean,

    /** Is the dialog currently open? */
    isOpen: boolean,

    usesOkButton?: boolean,
    usesCancelButton?: boolean,

    okButtonLabel?: string,
    cancelButtonLabel?: string,

    onOk?: (args: any) => void,
    onCancel?: () => void,
}

export type DialogActions = {
    /** Simple dialog with title and message */
    openSimpleDialog: (props: SimpleDialogProps) => void,

    /** Open the dialog with the given type and props */
    openTypedDialog: (type: DialogType, props: DialogProps) => void,

    /** Close the dialog, if open */
    close: () => void,
}

const initialState: DialogState = {
    dialogType: DialogType.Simple,
    componentProps: {},
    dialogResponse: null,
    background: 'white',
    title: '',
    message: '',
    isOpen: false,
    fitHeight: true,
    fitWidth: true,
    usesOkButton: false,
    usesCancelButton: false,
    okButtonLabel: 'Ok',
    cancelButtonLabel: 'Cancel',
    onOk: null,
    onCancel: null,
}

// Changed: createZustandStore -> createStore, and API for set has changed
export const dialogSlice = createStore(initialState, {name: 'dialog'})
    .extendActions(({ set, get }) => ({
        close: () => {
            // Changed: set.fieldName(value) -> set('fieldName', value)
            set('isOpen', false);
            set('dialogType', null);
            set('componentProps', null);
            set('usesOkButton', false);
            set('usesCancelButton', false);
            set('okButtonLabel', 'Ok');
            set('cancelButtonLabel', 'Cancel');
            set('onOk', null);
            set('onCancel', null);
            set('fitHeight', true);
            set('fitWidth', true);
            set('background', null);
            // Note, we do not clear out the dialogResponse, because we will need it!
        },
        openSimpleDialog: (props: SimpleDialogProps) => {
            set('dialogType', DialogType.Simple);
            set('title', props.title);
            set('message', props.message);
            set('okButtonLabel', props.okLabel || i18n.cs('ok', true));
            set('cancelButtonLabel', props.cancelLabel || i18n.cs('cancel', true));
            set('usesOkButton', !props.noButtons);
            set('background', props.background);
            // Changed: Access actions through get().actions or define close inline
            set('onOk', () => {
                set('isOpen', false);
                set('dialogType', null);
                set('componentProps', null);
                set('usesOkButton', false);
                set('usesCancelButton', false);
                set('okButtonLabel', 'Ok');
                set('cancelButtonLabel', 'Cancel');
                set('onOk', null);
                set('onCancel', null);
                set('fitHeight', true);
                set('fitWidth', true);
                set('background', null);
            });
            set('dialogResponse', null);
            set('isOpen', true);
            set('fitHeight', true);
            set('fitWidth', true);
        },
        openTypedDialog: (type: DialogType, props: DialogProps) => {
            set('dialogType', type);
            set('title', props.title);
            set('componentProps', props.componentProps);
            set('dialogResponse', null);
            set('background', props.background);
            set('usesCancelButton', props.useCancelButton);
            set('usesOkButton', props.useOkButton);
            set('onOk', props.onOk);
            set('onCancel', props.onCancel);
            set('okButtonLabel', props.okButtonLabel);
            set('cancelButtonLabel', props.cancelButtonLabel || i18n.cs('cancel', true));
            set('isOpen', true);
            set('fitWidth', props.fitWidth);
            set('fitHeight', props.fitHeight);
        },
    }));